/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * THE SECOND FACTOR, ENFORCED AT THE ROOT (hotfix, 05/10/2026).
 *
 * Production: an officer with a verified second factor signed in with email and
 * password, the two factor screen appeared, and refreshing the page went
 * straight into the dashboard. The screen was the only enforcement: on reload
 * the dashboard read the stored session and rendered it, at aal1.
 *
 * MfaGate wraps every page (the dashboard, the client portal, director review
 * and the rest). On every load and every auth state change it asks Supabase
 * for the session's assurance level. While a signed in user has a verified
 * factor and the session is aal1, it renders only the two factor challenge:
 * the page itself is not mounted, so nothing else renders and nothing is
 * fetched. Until the first check answers it renders nothing but a short
 * holding line. No session (a client on the portal) passes straight through.
 *
 * TWO FACTOR IS COMPULSORY (v1_969). Only officers sign in to Supabase (clients,
 * directors and second signatories use their own links). A signed in user with
 * no verified factor sees only the enrolment screen: scan the code, enter six
 * digits, and the session becomes aal2. Until then nothing else renders and no
 * case data is fetched; the server and the database refuse such a session too.
 */
import React from 'react';
import { supabase } from './supabase';

// Before the first check of a session: hold. 'open' passes the page through
// (no session, or a session at aal2); everything else is held.
export async function assuranceState(client = supabase) {
  const { data: { session } = {} } = await client.auth.getSession();
  if (!session) return { state: 'open' };
  const { data: aal, error } = await client.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error || !aal) return { state: 'unknown' };
  if (aal.currentLevel === 'aal2') return { state: 'open' };
  if (aal.nextLevel === 'aal2') {
    const { data: factors } = await client.auth.mfa.listFactors();
    const totp = ((factors && factors.totp) || []).find(f => f.status === 'verified') || null;
    return { state: 'challenge', factorId: totp ? totp.id : null, email: session.user && session.user.email };
  }
  // v1_969: signed in with no verified factor. Enrol before anything opens.
  return { state: 'enrol', email: session.user && session.user.email };
}

// v1_977: the gate looks like the dashboard sign in page (src/MLRODashboard.jsx,
// the login screen's page, cardStyle, inputStyle, btnStyle and header), so an
// officer moves from the password to the code on the same card. The values are
// copied, not imported: the dashboard is not loaded while the gate holds, and
// tools/harness-mfa-gate-look.mjs fails if the two drift apart. The palette
// variables come from theme-legacy.css, which is in the same bundle.
export const GATE_PAGE = { minHeight: "100vh", background: "var(--lc18)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter',sans-serif" };
export const GATE_CARD = { background: "#071727", border: "1px solid rgba(132,241,237,0.15)", borderRadius: 16, padding: "40px 48px", width: 400, textAlign: "center" };
export const GATE_INPUT = { width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(132,241,237,0.15)", borderRadius: 8, padding: "12px 16px", color: "#e8e8e0", fontSize: 14, fontFamily: "inherit", outline: "none" };
export const GATE_BUTTON = { width: "100%", background: "#13a4a4", border: "none", borderRadius: 8, padding: "12px", color: "#071727", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" };
const codeInput = { ...GATE_INPUT, fontSize: 22, letterSpacing: "8px", textAlign: "center", border: "1px solid var(--lc30)" };
const fieldLabel = { fontSize: 11, color: "var(--lc14)", marginBottom: 6, letterSpacing: "0.5px" };
const subtitle = { fontSize: 13, color: "var(--lc4)", marginBottom: 32 };
const note = { marginTop: 16, fontSize: 11, color: "var(--lc19)", lineHeight: 1.6 };
const instructions = { fontSize: 12, color: "var(--lc14)", lineHeight: 1.6, marginBottom: 20 };
const errorText = { fontSize: 12, color: "var(--lc24)", marginBottom: 12, textAlign: "left", lineHeight: 1.5 };
const backLink = { marginTop: 12, width: "100%", background: "transparent", border: "none", color: "var(--lc10)", fontSize: 11, cursor: "pointer", fontFamily: "inherit" };
const GateHeader = () => (
  <>
    <div style={{ fontSize: 11, color: "var(--lc2)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8 }}>CompliantID</div>
    <div style={{ fontSize: 20, fontWeight: 700, color: "var(--lc15)", marginBottom: 4, fontFamily: "Playfair Display,serif" }}>CompliantID</div>
  </>
);
// The sign in page follows the dashboard's theme (dark unless light was chosen
// in this browser). The dashboard is not mounted behind the gate, so the gate
// applies the same remembered choice itself, only while it is showing.
const useDashboardTheme = (active) => React.useEffect(() => {
  if (!active) return;
  try { document.documentElement.dataset.cidTheme = localStorage.getItem('cid_v2_theme') === 'light' ? 'light' : 'dark'; } catch (_) {}
}, [active]);

export default function MfaGate({ children }) {
  const [gate, setGate] = React.useState({ state: 'checking' });
  const [code, setCode] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const seq = React.useRef(0);
  const [enrolment, setEnrolment] = React.useState(null); // { id, qr, secret } | { failed: true }

  const check = React.useCallback(async () => {
    const n = ++seq.current;
    try {
      const g = await assuranceState();
      if (n === seq.current) setGate(g);
    } catch (_) {
      if (n === seq.current) setGate({ state: 'unknown' });
    }
  }, []);

  React.useEffect(() => {
    check();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      // A password reset arrives at aal1; the dashboard still needs to know it
      // was a reset once the second factor has been passed.
      if (event === 'PASSWORD_RECOVERY') window.__cidAuthRecovery = true;
      // Deferred: Supabase advises against calling auth methods inside this
      // callback. The page stays mounted meanwhile (an officer with no factor
      // keeps the enrolment prompt); the dashboard admits no user below aal2
      // itself, so nothing is fetched while this check runs.
      setTimeout(check, 0);
    });
    return () => subscription.unsubscribe();
  }, [check]);

  // v1_969: start (or restart) enrolment when the gate asks for it. A factor left
  // unverified by an earlier attempt is removed first, so a new one can be made.
  const startEnrolment = React.useCallback(async () => {
    setEnrolment(null); setError(''); setCode('');
    try {
      const { data: list } = await supabase.auth.mfa.listFactors();
      for (const f of ((list && list.all) || [])) {
        if (f && f.factor_type === 'totp' && f.status !== 'verified') { try { await supabase.auth.mfa.unenroll({ factorId: f.id }); } catch (_) {} }
      }
      const { data, error: eErr } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'CompliantID Dashboard' });
      if (eErr || !data || !data.totp) throw eErr || new Error('enrol');
      setEnrolment({ id: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
    } catch (_) {
      setEnrolment({ failed: true });
    }
  }, []);
  React.useEffect(() => {
    if (gate.state === 'enrol' && !enrolment) startEnrolment();
    if (gate.state !== 'enrol' && enrolment) setEnrolment(null);
  }, [gate.state, enrolment, startEnrolment]);

  const activate = async () => {
    if (code.length !== 6 || !enrolment || !enrolment.id) return;
    setBusy(true); setError('');
    try {
      const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId: enrolment.id });
      if (cErr || !challenge) throw cErr || new Error('challenge');
      const { error: vErr } = await supabase.auth.mfa.verify({ factorId: enrolment.id, challengeId: challenge.id, code });
      if (vErr) { setError('Code incorrect. Check your authenticator app and try again.'); setBusy(false); return; }
      setCode('');
      await check();
    } catch (_) {
      setError('The code could not be checked. Try again, or sign out and sign in again.');
    }
    setBusy(false);
  };

  const verify = async () => {
    if (!code.trim() || !gate.factorId) return;
    setBusy(true); setError('');
    try {
      const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId: gate.factorId });
      if (cErr || !challenge) throw cErr || new Error('challenge');
      const { error: vErr } = await supabase.auth.mfa.verify({ factorId: gate.factorId, challengeId: challenge.id, code: code.trim() });
      if (vErr) { setError('That code was not accepted. Check your authenticator app and try again.'); setBusy(false); return; }
      setCode('');
      await check();
    } catch (_) {
      setError('The code could not be checked. Try again, or sign out and sign in again.');
    }
    setBusy(false);
  };
  const signOut = async () => { await supabase.auth.signOut(); setCode(''); setError(''); check(); };

  if (gate.state === 'open') return children;
  return <GateScreen gate={gate} enrolment={enrolment} code={code} setCode={setCode} busy={busy} error={error}
    check={check} signOut={signOut} startEnrolment={startEnrolment} activate={activate} verify={verify} />;
}

// Everything the gate shows instead of the page. It is given no children, so
// the page cannot render from here whatever state it is in.
function GateScreen({ gate, enrolment, code, setCode, busy, error, check, signOut, startEnrolment, activate, verify }) {
  // Not while checking: a client on the portal (no session) passes through that
  // state on every load, and the officer's theme is no concern of theirs.
  useDashboardTheme(gate.state !== 'checking');
  const codeField = (onEnter) => (
    <div style={{ marginBottom: 8, textAlign: "left" }}>
      <div style={fieldLabel}>AUTHENTICATOR CODE</div>
      <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} autoFocus placeholder="000000"
        onChange={e => setCode(e.target.value.replace(/\D/g, ''))} onKeyDown={e => { if (e.key === 'Enter') onEnter(); }} aria-label="Authentication code"
        style={{ ...codeInput, border: error ? "1px solid var(--lc55)" : codeInput.border }} />
    </div>
  );
  const back = <button style={backLink} onClick={signOut}>← Back to sign in</button>;

  if (gate.state === 'checking') return (
    <div data-mfa-gate="checking" style={GATE_PAGE}>
      <div style={{ fontSize: 13, color: "var(--lc4)" }}>Checking your sign in</div>
    </div>
  );
  if (gate.state === 'unknown') return (
    <div data-mfa-gate="unknown" style={GATE_PAGE}>
      <div style={GATE_CARD}>
        <GateHeader />
        <div style={subtitle}>Your sign in could not be confirmed</div>
        <div style={instructions}>We could not check whether this sign in has passed two factor authentication, so nothing is shown. Try again, or sign out and sign in again.</div>
        <button style={GATE_BUTTON} onClick={check}>Try again</button>
        {back}
      </div>
    </div>
  );
  if (gate.state === 'enrol') return (
    <div data-mfa-gate="enrol" style={GATE_PAGE}>
      <div style={GATE_CARD}>
        <GateHeader />
        <div style={{ ...subtitle, marginBottom: 16 }}>Set up two factor authentication</div>
        <div style={instructions}>
          This dashboard holds sensitive compliance data and requires two factor authentication for every officer account{gate.email ? ' (' + gate.email + ')' : ''}. Scan the code with your authenticator app (Google Authenticator, Authy or 1Password), then enter the six digit code it shows. Nothing is shown until this is done.
        </div>
        {enrolment && enrolment.failed ? (
          <>
            <div style={errorText}>Two factor setup could not be started. Try again, or contact compliance@compliantid.com.</div>
            <button style={GATE_BUTTON} onClick={startEnrolment}>Try again</button>
          </>
        ) : !enrolment ? (
          <div style={{ fontSize: 12, color: "var(--lc4)", marginBottom: 12 }}>Preparing your code</div>
        ) : (
          <>
            <div style={{ display: "inline-block", background: "#ffffff", borderRadius: 8, padding: 8, marginBottom: 12 }}><img src={enrolment.qr} alt="Authenticator setup code" style={{ width: 160, height: 160, display: "block" }} /></div>
            <div style={{ ...fieldLabel, textAlign: "left" }}>MANUAL ENTRY KEY</div>
            <div style={{ fontSize: 12, fontFamily: "monospace", letterSpacing: 2, wordBreak: "break-all", color: "var(--lc7)", textAlign: "left", marginBottom: 16 }}>{enrolment.secret}</div>
            {codeField(activate)}
            {error && <div style={errorText}>{error}</div>}
            <button style={{ ...GATE_BUTTON, marginTop: 8, opacity: busy || code.length !== 6 ? 0.5 : 1 }} disabled={busy || code.length !== 6} onClick={activate}>{busy ? 'Checking' : 'Activate Two Factor Authentication'}</button>
          </>
        )}
        {back}
      </div>
    </div>
  );
  return (
    <div data-mfa-gate="challenge" style={GATE_PAGE}>
      <div style={GATE_CARD}>
        <GateHeader />
        <div style={subtitle}>Two factor authentication required</div>
        {gate.factorId ? (
          <>
            {codeField(verify)}
            {error && <div style={errorText}>{error}</div>}
            <button style={{ ...GATE_BUTTON, marginTop: 8, opacity: busy || code.length !== 6 ? 0.5 : 1 }} disabled={busy || code.length !== 6} onClick={verify}>{busy ? 'Checking' : 'Verify'}</button>
            <div style={note}>Enter the six digit code from your authenticator app{gate.email ? ' for ' + gate.email : ''}. Nothing is shown until the code is accepted.</div>
          </>
        ) : (
          <div style={errorText}>No authenticator app is set up for this sign in. Contact compliance@compliantid.com.</div>
        )}
        {back}
      </div>
    </div>
  );
}
